// Global kill-switch for the slow-scroll ad-snap effect. Independent of the bazaar
// IS_SLOW_SCROLL_ENABLED flag, which now only gates the slow-scroll CSS/layout.
export const IS_AD_SNAP_ENABLED = true;

// Snap scroll to an AdSlot when it enters the viewport (scrolling down). Each ad to be
// snapped must be wrapped in an element carrying `data-ad-snap`, inside `container`.
// Returns a teardown that removes listeners and timers.
export function attachAdSnap(container: HTMLElement): () => void {
    // Users who opt out of motion still get the ad snapped into view, but instantly.
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const snapBehavior: ScrollBehavior = reduceMotion ? "auto" : "smooth";

    const snapped = new Set<HTMLElement>();
    let isAnimating = false;
    let lastScrollY = window.scrollY;
    let rafId = 0;
    // Fallback timer in case `scrollend` never fires (older Safari, interrupted anim).
    let animFallback = 0;

    const endAnimation = () => {
        window.clearTimeout(animFallback);
        isAnimating = false;
        // Resync so the next direction check isn't skewed by the programmatic jump.
        lastScrollY = window.scrollY;
    };

    const onScroll = () => {
        // Ignore scroll events generated by our own programmatic snap.
        if (isAnimating) return;
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
            const y = window.scrollY;
            const scrollingDown = y > lastScrollY;
            lastScrollY = y;
            if (!scrollingDown) return;

            const adEls = container.querySelectorAll<HTMLElement>("[data-ad-snap]");
            for (const el of adEls) {
                if (snapped.has(el)) continue;
                const rect = el.getBoundingClientRect();
                if (rect.top >= 0 && rect.top < 150) {
                    snapped.add(el);
                    isAnimating = true;
                    window.scrollTo({ top: rect.top + y - 120, behavior: snapBehavior });
                    // `scrollend` clears the flag reliably; timer is just a safety net.
                    animFallback = window.setTimeout(endAnimation, 1500);
                    break;
                }
            }
        });
    };

    const onScrollEnd = () => {
        if (isAnimating) endAnimation();
    };

    // Any manual input aborts the snap so the user can scroll past freely.
    const onUserInput = () => {
        if (isAnimating) endAnimation();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("scrollend", onScrollEnd, { passive: true });
    window.addEventListener("wheel", onUserInput, { passive: true });
    window.addEventListener("touchstart", onUserInput, { passive: true });
    window.addEventListener("keydown", onUserInput);
    return () => {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("scrollend", onScrollEnd);
        window.removeEventListener("wheel", onUserInput);
        window.removeEventListener("touchstart", onUserInput);
        window.removeEventListener("keydown", onUserInput);
        cancelAnimationFrame(rafId);
        window.clearTimeout(animFallback);
        snapped.clear();
    };
}
